import { z } from 'zod';
import { SeveritySchema } from './events.js';
/**
 * TechSpec §3.2 (rules, alerts, alert_transitions), §6.1 (routes), §12 (engine);
 * PRD Epic F (VS-060…063). Step 30 ships the engine and the read side; the
 * lifecycle endpoints (ack, snooze, resolve) are step 31 and delivery is step 32.
 */
export const RULE_CONDITION_TYPES = [
    'metric_threshold',
    'event_match',
    'source_stale',
    'absence',
];
export const THRESHOLD_OPERATORS = ['gt', 'gte', 'lt', 'lte'];
const Name = z.string().min(1).max(120);
const Seconds = (min, max) => z.number().int().min(min).max(max);
const IsoDate = z.iso.datetime({ offset: true });
export const RuleConditionSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('metric_threshold'),
        sourceId: z.uuid(),
        metricName: Name,
        tags: z.record(z.string(), z.string()).optional(),
        operator: z.enum(THRESHOLD_OPERATORS),
        value: z.number(),
        forSeconds: Seconds(0, 86_400),
    }),
    z.object({
        type: z.literal('event_match'),
        sourceId: z.uuid().optional(),
        kinds: z.array(Name).max(20).optional(),
        minSeverity: SeveritySchema.optional(),
        titleContains: z.string().min(1).max(200).optional(),
    }),
    z.object({
        type: z.literal('source_stale'),
        sourceId: z.uuid(),
        staleAfterSeconds: Seconds(60, 604_800),
    }),
    z.object({
        type: z.literal('absence'),
        sourceId: z.uuid(),
        kind: Name,
        withinSeconds: Seconds(60, 604_800),
    }),
]);
/** The source a condition is about, when it names one; null for a global event_match. */
export const conditionSourceId = (condition) => 'sourceId' in condition && condition.sourceId ? condition.sourceId : null;
/** Delivery channels (TechSpec §12.3); delivery itself arrives in step 32. */
export const NOTIFICATION_CHANNELS = ['in-app', 'desktop', 'telegram'];
export const RuleSchema = z.object({
    id: z.uuid(),
    name: Name,
    enabled: z.boolean(),
    sourceId: z.uuid().nullable(),
    condition: RuleConditionSchema,
    severity: SeveritySchema,
    channels: z.array(z.enum(NOTIFICATION_CHANNELS)),
    cooldownS: Seconds(0, 86_400),
    /** Step 32: a severity-4 rule may notify desktop and Telegram during quiet hours (TechSpec §12.3). */
    quietHoursOverride: z.boolean(),
    throttledUntil: IsoDate.nullable(),
    createdAt: IsoDate,
    updatedAt: IsoDate,
});
export const RuleListResponseSchema = z.object({ rules: z.array(RuleSchema) });
export const CreateRuleRequestSchema = z.object({
    name: Name,
    enabled: z.boolean().default(true),
    condition: RuleConditionSchema,
    severity: SeveritySchema.default(2),
    channels: z.array(z.enum(NOTIFICATION_CHANNELS)).default(['in-app']),
    cooldownS: Seconds(0, 86_400).default(300),
    quietHoursOverride: z.boolean().default(false),
});
/**
 * PATCH body. Setting `enabled` also clears `throttledUntil`. Spelled out rather
 * than `CreateRuleRequestSchema.partial()`: Zod 4 applies the defaults inside
 * optional fields, so `{ name }` would also reset `enabled`, `severity`…
 */
export const UpdateRuleRequestSchema = z.object({
    name: Name.optional(),
    enabled: z.boolean().optional(),
    condition: RuleConditionSchema.optional(),
    severity: SeveritySchema.optional(),
    channels: z.array(z.enum(NOTIFICATION_CHANNELS)).optional(),
    cooldownS: Seconds(0, 86_400).optional(),
    quietHoursOverride: z.boolean().optional(),
});
export const ALERT_STATUSES = ['open', 'acknowledged', 'snoozed', 'resolved'];
/** Statuses that hold the fingerprint: a new fire updates them instead of opening a twin. */
export const ACTIVE_ALERT_STATUSES = ['open', 'acknowledged', 'snoozed'];
export const AlertSchema = z.object({
    id: z.uuid(),
    ruleId: z.uuid().nullable(),
    ruleName: z.string().nullable(),
    sourceId: z.uuid().nullable(),
    status: z.enum(ALERT_STATUSES),
    severity: SeveritySchema,
    title: z.string(),
    body: z.string().nullable(),
    context: z.record(z.string(), z.unknown()),
    fingerprint: z.string(),
    fireCount: z.number().int().min(1),
    openedAt: IsoDate,
    lastFiredAt: IsoDate,
    ackedAt: IsoDate.nullable(),
    snoozedUntil: IsoDate.nullable(),
    resolvedAt: IsoDate.nullable(),
    lastNotifiedAt: IsoDate.nullable(),
});
export const ALERT_TRANSITION_TYPES = [
    'opened',
    'fired',
    'acknowledged',
    'snoozed',
    'resolved',
    'notified',
    'throttled',
    /** A snooze ran out: the alert is open again (step 31). */
    'unsnoozed',
    /** A channel gave up after its retries (step 32): context `{channel, attempts, error}`. */
    'delivery_failed',
];
export const AlertTransitionSchema = z.object({
    id: z.uuid(),
    alertId: z.uuid().nullable(),
    ruleId: z.uuid().nullable(),
    type: z.enum(ALERT_TRANSITION_TYPES),
    at: IsoDate,
    context: z.record(z.string(), z.unknown()),
});
export const AlertListQuerySchema = z.object({
    status: z.enum(ALERT_STATUSES).optional(),
    ruleId: z.uuid().optional(),
    sourceId: z.uuid().optional(),
    minSeverity: z.coerce.number().int().min(0).max(4).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    /** Keyset pagination on openedAt, newest first. */
    before: IsoDate.optional(),
});
export const AlertListResponseSchema = z.object({ alerts: z.array(AlertSchema) });
export const AlertDetailResponseSchema = z.object({
    alert: AlertSchema,
    transitions: z.array(AlertTransitionSchema),
});
/**
 * Lifecycle (step 31, PRD VS-062/063). `POST /alerts/:id/ack|snooze|resolve`
 * answer the updated AlertDto; a resolved alert answers 409 CONFLICT to ack and
 * snooze. Ack of an acknowledged alert and resolve of a resolved one are no-ops (200).
 */
export const SNOOZE_MAX_MINUTES = 10_080;
export const SnoozeRequestSchema = z.object({
    untilMinutes: z.number().int().min(1).max(SNOOZE_MAX_MINUTES),
});
/** `POST /alerts/ack`: acknowledges the active ones, skips the rest. */
export const BulkAckRequestSchema = z.object({ ids: z.array(z.uuid()).min(1).max(200) });
export const BulkAckResponseSchema = z.object({ acknowledged: z.number().int().min(0) });
/** `GET /alerts/noisiest`: rules by fires (opened + fired transitions) over the window (VS-063). */
export const NoisiestRulesQuerySchema = z.object({
    days: z.coerce.number().int().min(1).max(31).default(7),
    limit: z.coerce.number().int().min(1).max(50).default(10),
});
export const NoisiestRulesResponseSchema = z.object({
    from: IsoDate,
    to: IsoDate,
    rules: z.array(z.object({
        ruleId: z.uuid(),
        /** Null when the rule was deleted since. */
        ruleName: z.string().nullable(),
        fires: z.number().int().min(0),
        alerts: z.number().int().min(0),
    })),
});
/** Default and ceiling of the backtest window (PRD VS-060: "would this have fired last week?"). */
export const BACKTEST_DEFAULT_DAYS = 7;
export const BACKTEST_MAX_DAYS = 31;
export const BacktestRequestSchema = z.object({
    /** Tests this condition instead of the saved one; required on the unsaved-rule route. */
    condition: RuleConditionSchema.optional(),
    from: IsoDate.optional(),
    to: IsoDate.optional(),
});
export const BacktestResponseSchema = z.object({
    /** false for source_stale and absence: there is no history to replay. */
    supported: z.boolean(),
    from: IsoDate,
    to: IsoDate,
    fires: z.number().int().min(0),
    samples: z.array(z.object({ at: IsoDate, title: z.string() })).max(20),
});
/** Rule auto-throttle (TechSpec §12.2, PRD VS-063): fires per hour before a rule is paused. */
export const THROTTLE_FIRES_PER_HOUR = 10;
export const THROTTLE_PAUSE_S = 3600;
/** event_match alerts auto-resolve after this long without a new match (TechSpec §12.2). */
export const EVENT_MATCH_IDLE_RESOLVE_S = 3600;
//# sourceMappingURL=alerts.js.map